/**
 * EPIC-DM01: Demo Mode Button
 *
 * Outdoor-optimised button with large touch targets, bold text, and
 * variant/size support via class-variance-authority.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Variants                                                          */
/* ------------------------------------------------------------------ */

const demoButtonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-xl font-bold',
    'text-demo-base transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-demo-green focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.97]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-demo-green text-white hover:bg-demo-green-dark shadow-demo-sm hover:shadow-demo-md',
        secondary:
          'bg-white text-demo-green border-2 border-demo-green hover:bg-demo-green-light shadow-demo-sm',
        outline:
          'border-2 border-demo-text-muted text-demo-text bg-transparent hover:bg-demo-surface-alt',
        ghost:
          'text-demo-green bg-transparent hover:bg-demo-green-light',
      },
      size: {
        sm: 'min-h-[44px] px-4 py-2 text-[16px]',
        md: 'min-h-[var(--demo-touch-min)] px-6 py-3',
        lg: 'min-h-[var(--demo-touch-comfortable)] px-8 py-4',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      fullWidth: false,
    },
  },
);

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface DemoButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof demoButtonVariants> {
  /** Show a loading spinner and disable interaction */
  loading?: boolean;
  /** Icon rendered before children */
  iconLeft?: React.ReactNode;
  /** Icon rendered after children */
  iconRight?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const DemoButton = React.forwardRef<HTMLButtonElement, DemoButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading = false,
      disabled,
      iconLeft,
      iconRight,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(demoButtonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          iconLeft
        )}
        {children}
        {!loading && iconRight}
      </button>
    );
  },
);

DemoButton.displayName = 'DemoButton';

export { DemoButton, demoButtonVariants };
